<!--
  Login — the one sign-in screen for every kind of session.

  The "Login" field takes either a project code (crew or client password) or a
  company account login (e.g. the mix design library). The server resolves
  which one it is and which hash matched; there is nothing on this screen that
  tells a user which kind of session they'll get, which is deliberate — a
  failed attempt must not reveal whether the identifier exists at all.

  Arrival via a share link does NOT come through here; App.svelte handles the
  /share/:code/:token screen and only falls back to this form if the token is
  revoked or unknown.
-->
<script lang="ts">
  import { isApiError } from '../lib/api';
  import { login, rememberProjectCode, rememberedProjectCode } from '../lib/store';

  interface Props {
    /** Pre-fills the code (e.g. after a share token was rejected). */
    initialCode?: string;
    /** Shown above the form, e.g. 'That client link has been revoked.' */
    message?: string | null;
  }

  let { initialCode = '', message = null }: Props = $props();

  let code = $state(initialCode || rememberedProjectCode());
  let password = $state('');
  // Reveal is opt-in per sign-in and never persists — a shoulder on a job site
  // is the likelier threat here than a forgotten keystroke.
  let showPassword = $state(false);
  let busy = $state(false);
  let error = $state<string | null>(null);
  let fieldErrors = $state<Record<string, string>>({});

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (busy) return;

    error = null;
    fieldErrors = {};

    if (!code.trim()) {
      fieldErrors = { code: 'Enter your login.' };
      return;
    }
    if (!password) {
      fieldErrors = { password: 'Enter the password.' };
      return;
    }

    busy = true;
    try {
      await login(code.trim(), password);
      rememberProjectCode(code);
      password = '';
      showPassword = false;
    } catch (cause) {
      if (isApiError(cause)) {
        error =
          cause.code === 'rate_limited'
            ? 'Too many attempts. Wait a few minutes and try again.'
            : cause.code === 'unauthorized'
              ? 'That login and password do not match.'
              : cause.message;
        fieldErrors = cause.fields ?? {};
      } else {
        error = 'Could not sign in. Check the signal and try again.';
      }
    } finally {
      busy = false;
    }
  }
</script>

<div class="screen">
  <div class="brand">
    <h1>SCI Field QC</h1>
    <p class="meta">Sustainable Concrete Inc. — field quality records</p>
  </div>

  {#if message}
    <div class="banner banner--warn" role="status">
      <div>{message}</div>
    </div>
  {/if}

  {#if error}
    <div class="banner banner--error" role="alert">
      <div><strong>Cannot sign in</strong>{error}</div>
    </div>
  {/if}

  <form class="card" onsubmit={submit} novalidate>
    <div class="card__body">
      <label class="field">
        <span class="label">Login</span>
        <!--
          `name="code"` stays as-is even though this field now takes an account
          login too: it is not user-visible, and renaming it buys nothing.
        -->
        <input
          type="text"
          name="code"
          bind:value={code}
          autocapitalize="none"
          autocorrect="off"
          spellcheck="false"
          autocomplete="username"
          enterkeyhint="next"
          placeholder="KiewitWF or sciquality"
          aria-invalid={fieldErrors.code ? 'true' : undefined}
        />
        {#if fieldErrors.code}<span class="field__flag">{fieldErrors.code}</span>{/if}
      </label>

      <!--
        Not `bind:value`: Svelte refuses a two-way binding on an input whose
        `type` is dynamic, so the reveal toggle drives the value by hand.
        The button lives outside the <label> because a nested control would
        swallow the label's click-to-focus.
      -->
      <div class="field">
        <label class="label" for="password">Password</label>
        <div class="pw">
          <input
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={password}
            oninput={(event) => (password = event.currentTarget.value)}
            autocomplete="current-password"
            autocapitalize="none"
            autocorrect="off"
            spellcheck="false"
            enterkeyhint="go"
            aria-invalid={fieldErrors.password ? 'true' : undefined}
          />
          <button
            type="button"
            class="btn btn--ghost pw__toggle"
            onclick={() => (showPassword = !showPassword)}
            aria-controls="password"
            aria-pressed={showPassword}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {#if fieldErrors.password}<span class="field__flag">{fieldErrors.password}</span>{/if}
      </div>

      <button class="btn btn--primary btn--block btn--big" type="submit" disabled={busy}>
        {#if busy}<span class="spinner" aria-hidden="true"></span> Signing in…{:else}Sign in{/if}
      </button>

      <p class="meta center foot">
        Clients can also open the read-only link SCI sent them.
      </p>
    </div>
  </form>
</div>

<style>
  .brand {
    text-align: center;
    padding: 2rem 0 1.25rem;
  }

  .foot {
    margin: 0.9rem 0 0;
  }

  .pw {
    position: relative;
    display: flex;
    align-items: center;
  }

  /* Room for the toggle, so a long password never runs under it. */
  .pw input {
    padding-right: 4.5rem;
  }

  /* Edge draws its own reveal eye; two of them is one too many. */
  .pw input::-ms-reveal {
    display: none;
  }

  /* Inset to the input's 2px border so the target is the full field height —
     this gets tapped with gloves on. */
  .pw__toggle {
    position: absolute;
    top: 2px;
    right: 2px;
    bottom: 2px;
    min-height: 0;
    padding: 0 0.7rem;
    font-size: 0.85rem;
    border-radius: 0 calc(var(--radius) - 2px) calc(var(--radius) - 2px) 0;
  }
</style>
